import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./AdminShared.css";
import { API_BASE_URL } from "../config";

export default function AdminIVForm() {
  const navigate = useNavigate();

  const [ivForms, setIvForms] = useState([]);
  const [filter, setFilter] = useState("All");
  const [visitFilter, setVisitFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const fetchIVForms = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/ivform`);
      if (!res.ok) throw new Error("Synchronization failed");
      const data = await res.json();
      setIvForms(
        Array.isArray(data)
          ? data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          : [],
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const adminInfo = localStorage.getItem("adminInfo");
    if (!adminInfo) return navigate("/admin");
    fetchIVForms();
  }, [fetchIVForms, navigate]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/ivform/banner`)
      .then((res) => res.json())
      .then((data) => {
        // Banner logic abstracted from this node
      })
      .catch(() => console.log("IV banner load failed"));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Purge institutional record?")) return;
    try {
      setDeleting(id);
      await fetch(`${API_BASE_URL}/api/ivform/${id}`, { method: "DELETE" });
      setIvForms((prev) => prev.filter((form) => form._id !== id));
    } catch (err) {
      alert("❌ Operation failed");
    } finally {
      setDeleting(null);
    }
  };

  const exportToExcel = () => {
    const worksheetData = filteredData.map((f, i) => ({
      "S.No": i + 1,
      Name: f.name,
      Email: f.email,
      Phone: f.phone,
      Role: f.role,
      Designation: f.designation || "-",
      "Employee ID": f.employeeId || "-",
      "Register Number": f.register_number || "-",
      Department: f.department || "-",
      Year: f.year || "-",
      College: f.college || "-",
      "Visit Type": f.visit_type,
      "Visit Date": f.visit_date
        ? new Date(f.visit_date).toLocaleDateString("en-GB")
        : "-",
      "Training Duration": f.training_duration || "-",
      Purpose: f.purpose || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "IV Forms");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    saveAs(new Blob([excelBuffer]), "FlyTutor_IV_Matrix.xlsx");
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await fetch(`${API_BASE_URL}/api/ivform/upload-image`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        alert("✅ Educational Banner Deployed.");
      }
    } catch (err) {
      alert("Deployment failed");
    }
  };

  const filteredData = ivForms.filter((f) => {
    const roleMatch =
      filter === "All" || f.role?.toLowerCase() === filter.toLowerCase();
    const visitMatch =
      visitFilter === "All" ||
      f.visit_type?.toLowerCase() === visitFilter.toLowerCase();
    return roleMatch && visitMatch;
  });

  if (loading)
    return (
      <div className="admin-list-container">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "80vh",
            gap: "20px",
          }}
        >
          <div className="loading-spinner"></div>
          <p style={{ fontWeight: 700, color: "#475569" }}>
            ⏳ Synchronizing Institutional Liaison Matrix...
          </p>
        </div>
      </div>
    );

  return (
    <div className="admin-list-container">
      <header className="admin-list-header">
        <div className="header-left-group">
          <img src="/logo.png" alt="FlyTutor" className="admin-header-logo" />
          <h1 className="admin-header-title">IV Operations Ledger</h1>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className="module-badge"
            style={{
              cursor: "pointer",
              background: "#84cc16",
              border: "none",
              color: "#fff",
            }}
            onClick={exportToExcel}
          >
            ⬇ Export Matrix
          </button>
          <input
            type="file"
            accept="image/*"
            id="ivImageUpload"
            style={{ display: "none" }}
            onChange={handleImageSelect}
          />
          <button
            className="module-badge"
            style={{
              cursor: "pointer",
              background: "#475569",
              border: "none",
              color: "#fff",
            }}
            onClick={() => document.getElementById("ivImageUpload").click()}
          >
            📤 Deploy Banner
          </button>
        </div>
      </header>

      <div className="admin-list-content">
        <div className="admin-info-bar">
          <div className="admin-user-tag">
            <div className="dot" style={{ background: "#84cc16" }}></div>
            Operator: Institutional Liaison Node
          </div>
          <div
            className="filter-group-elite"
            style={{ display: "flex", gap: "10px" }}
          >
            <select
              className="elite-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="All">All Roles</option>
              <option value="Student">Students</option>
              <option value="Staff">Faculty</option>
            </select>
            <select
              className="elite-select"
              value={visitFilter}
              onChange={(e) => setVisitFilter(e.target.value)}
            >
              <option value="All">All Visit Types</option>
              <option value="industrial_visit">Industrial Visit</option>
              <option value="inplant_training">Inplant Training</option>
            </select>
          </div>
          <div
            className="admin-status-badge"
            style={{
              background: "#f0fdf4",
              color: "#166534",
              fontSize: "0.75rem",
              fontWeight: 800,
            }}
          >
            {filteredData.length} INSTITUTIONAL RECORDS
          </div>
        </div>

        <div className="admin-table-wrapper" style={{ overflowX: "auto" }}>
          <table className="elite-table">
            <thead>
              <tr>
                <th>Profile Ident</th>
                <th>Academic Node</th>
                <th>Coordinators</th>
                <th>Temporal Scan</th>
                <th>Logistics</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      padding: "100px",
                      color: "#94a3b8",
                    }}
                  >
                    No institutional fragments detected in the current buffer.
                  </td>
                </tr>
              ) : (
                filteredData.map((form) => (
                  <tr key={form._id}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{form.name}</div>
                      <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                        {form.role?.toUpperCase()}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                        {form.register_number || form.employeeId}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{form.college}</div>
                      <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                        {form.department}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: "0.8rem" }}>{form.email}</div>
                      <div style={{ fontWeight: 700, color: "#84cc16" }}>
                        {form.phone}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {new Date(form.createdAt).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                        SYNC: {new Date(form.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: "#475569" }}>
                        {form.visit_type?.replace(/_/g, " ").toUpperCase()}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                        {form.visit_date
                          ? `DATE: ${new Date(form.visit_date).toLocaleDateString()}`
                          : `DUR: ${form.training_duration?.toUpperCase()}`}
                      </div>
                    </td>
                    <td>
                      <span
                        className="location-badge"
                        style={{
                          background: "#f1f5f9",
                          color: "#475569",
                          fontSize: "0.65rem",
                        }}
                      >
                        {form.preferred_location?.toUpperCase() || "REMOTE"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {form.excelFilePath && (
                          <a
                            href={form.excelFilePath} 
                            download
                            className="action-btn-elite"
                            style={{
                              background: "#84cc16",
                              color: "#fff",
                              textDecoration: "none",
                            }}
                          >
                            ⬇
                          </a>
                        )}
                        <button
                          className="action-btn-elite btn-delete"
                          onClick={() => handleDelete(form._id)}
                          disabled={deleting === form._id}
                        >
                          {deleting === form._id ? "..." : "🗑️"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
